<template>
  <v-snackbar
    v-model="showSnackbar"
    :timeout="-1"
    bottom
    right
    color="primary"
    max-width="380"
    min-width="320"
  >
    <div class="d-flex align-center">
      <v-progress-circular indeterminate size="20" width="2" color="white" class="mr-3" />
      <div>
        <div class="font-weight-bold white--text" style="font-size: 14px;">
          พบเวอร์ชันใหม่ กำลังอัปเดตอัตโนมัติ...
        </div>
        <div class="white--text mt-1" style="font-size: 12px; opacity: 0.9;">
          {{ currentVersion }} → {{ latestVersion }}
        </div>
      </div>
    </div>
  </v-snackbar>
</template>

<script>
/**
 * VersionUpdateNotifier
 *
 * ตรวจสอบ version จาก /version.json เป็นระยะ เมื่อพบว่าเวอร์ชันบน server
 * ไม่ตรงกับเวอร์ชันที่ผู้ใช้กำลังใช้อยู่ จะล้าง cache/service worker แล้ว
 * reload หน้าเว็บให้อัตโนมัติทันที ไม่มีการถามผู้ใช้ก่อน
 */
export default {
  name: 'VersionUpdateNotifier',

  data() {
    return {
      showSnackbar: false,
      updating: false,
      checkInterval: null,

      currentVersion: '',
      latestVersion: ''
    }
  },

  mounted() {
    this.initVersion()
  },

  beforeDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
  },

  methods: {
    async initVersion() {
      await this.$nextTick()

      if (this.$appVersion && this.$appVersion.version && this.$appVersion.version !== '1.0.0') {
        this.currentVersion = this.$appVersion.version
      } else {
        try {
          const res = await fetch('/version.json?t=' + Date.now())
          const data = await res.json()
          this.currentVersion = data.version || '1.0.0'
        } catch (err) {
          this.currentVersion = '1.0.0'
        }
      }

      // ใช้ localStorage เก็บ version ที่ user ใช้อยู่จริง
      // ป้องกัน: หลัง deploy ใหม่ → version.json เปลี่ยน → $appVersion โหลดค่าใหม่มา
      // ทำให้ current = latest → ไม่เจอว่ามีอัปเดต (false negative)
      const savedVersion = localStorage.getItem('app_current_version')
      if (savedVersion) {
        this.currentVersion = savedVersion
      } else {
        localStorage.setItem('app_current_version', this.currentVersion)
      }

      // Check ครั้งแรกหลัง 5 วินาที
      setTimeout(() => {
        this.checkForUpdate()
      }, 5000)

      // Check ทุก 5 นาที
      this.checkInterval = setInterval(() => {
        this.checkForUpdate()
      }, 5 * 60 * 1000)

      // Check เมื่อ user กลับมาที่ tab
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    },

    async checkForUpdate() {
      if (this.updating) return

      try {
        const res = await fetch('/version.json?t=' + Date.now())

        if (!res.ok) {
          throw new Error('HTTP ' + res.status)
        }

        const contentType = res.headers.get('content-type')
        if (contentType && !contentType.includes('application/json')) {
          throw new Error('Invalid content type')
        }

        const data = await res.json()

        if (!data.version) {
          throw new Error('No version in response')
        }

        this.latestVersion = data.version

        if (this.currentVersion !== data.version) {
          console.log('🚀 Update available:', this.currentVersion, '→', data.version)
          this.applyUpdate()
        }
      } catch (err) {
        console.error('Version check failed:', err)
      }
    },

    onVisibilityChange() {
      if (!document.hidden) {
        this.checkForUpdate()
      }
    },

    async applyUpdate() {
      if (this.updating) return
      this.updating = true
      this.showSnackbar = true

      try {
        // อัปเดท current version ก่อน reload
        localStorage.setItem('app_current_version', this.latestVersion)

        // ล้าง Cache API
        if ('caches' in window) {
          const cacheNames = await caches.keys()
          await Promise.all(cacheNames.map(name => caches.delete(name)))
          console.log('✅ Caches cleared:', cacheNames.length, 'entries')
        }

        // Unregister Service Workers
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations()
          await Promise.all(registrations.map(reg => reg.unregister()))
          console.log('✅ Service workers unregistered:', registrations.length)
        }
      } catch (err) {
        console.error('Update cleanup failed:', err)
      }

      // หน่วงสั้นๆ ให้ snackbar ทันแสดงก่อน reload
      setTimeout(() => {
        window.location.reload(true)
      }, 1200)
    }
  }
}
</script>
